/**
 * RBAC helper utilities for frontend
 *
 * Provides utilities for checking permissions and improving UX
 */

import { isPermissionDeniedError } from './apiErrorHandler'

/**
 * Check if current user can edit/delete an asset
 *
 * @param assetCreatedBy - The user ID who created the asset
 * @param currentUserId - Current user's ID
 * @param currentUser - Current user object (optional, to check admin status)
 * @returns True if user can edit/delete (is owner or admin)
 *
 * Note: Admin check should be done server-side, but this helps with UI indicators
 */
export function canUserModifyAsset(
  assetCreatedBy: string | null | undefined,
  currentUserId: string | null,
  currentUser?: { roles?: Array<{ name: string }> } | null
): boolean {
  if (!currentUserId) {
    return false
  }

  // Admins can modify any asset
  if (currentUser && isUserAdmin(currentUser)) {
    return true
  }

  // Users can modify assets they created
  if (!assetCreatedBy) {
    return false
  }

  return assetCreatedBy === currentUserId
}

/**
 * Get empty state message based on context
 *
 * @param resourceName - Name of the resource (e.g., "models", "prompts")
 * @param hasError - Whether there was an error loading
 * @param error - The error object if any
 * @param isSearching - Whether user is searching/filtering
 * @returns Appropriate empty state message
 */
export interface EmptyStateMessageOverrides {
  /** Translated text for the permission-denied case; falls back to the English default when omitted. */
  permissionDenied?: string
  /** Translated text for the "no results match your search" case; falls back to the English default when omitted. */
  noResultsSearch?: string
  /** Translated text for the default (no error, not searching) case; falls back to the English default when omitted. */
  default?: string
}

export function getEmptyStateMessage(
  resourceName: string,
  hasError: boolean = false,
  error: unknown = null,
  isSearching: boolean = false,
  overrides?: EmptyStateMessageOverrides
): string {
  // If there's a permission error, show access-related message
  if (hasError && error && isPermissionDeniedError(error)) {
    return overrides?.permissionDenied ?? `You don't have permission to view ${resourceName}. Contact your administrator for access.`
  }

  // If searching, show search-specific message
  if (isSearching) {
    return overrides?.noResultsSearch ?? `No ${resourceName} found matching your search.`
  }

  // Default empty state
  return overrides?.default ?? `No ${resourceName} found. Create your first ${resourceName.slice(0, -1)} to get started.`
}

/**
 * Get resource name from error context
 *
 * @param endpoint - API endpoint that failed
 * @returns Resource name (e.g., "model", "prompt", "tool")
 */
export function getResourceNameFromEndpoint(endpoint: string): string {
  const resourceMap: Record<string, string> = {
    '/models': 'model',
    '/prompts': 'prompt',
    '/tools': 'tool',
    '/knowledge': 'knowledge connector',
    '/connections': 'connection',
    '/agents': 'agent',
    '/projects': 'project',
  }

  for (const [path, resource] of Object.entries(resourceMap)) {
    if (endpoint.includes(path)) {
      return resource
    }
  }

  return 'resource'
}

/**
 * Get current user ID from auth context
 * Note: This should be called from within a component that has access to AuthContext
 * For use in utilities, pass currentUserId as a parameter instead
 */
export function getCurrentUserId(currentUser: { id: string } | null): string | null {
  return currentUser?.id || null
}

/**
 * Check if user has admin role (Admin or Platform Admin)
 *
 * @param currentUser - Current user object with roles array
 * @returns True if user has Admin or Platform Admin role
 */
export function isUserAdmin(
  currentUser: { roles?: Array<{ name: string }>; is_platform_admin?: boolean } | null,
): boolean {
  if (!currentUser) {
    return false
  }

  if (currentUser.is_platform_admin) {
    return true
  }

  if (!currentUser.roles || currentUser.roles.length === 0) {
    return false
  }
  // Check if any role has name "Admin" or "Platform Admin" (case-insensitive)
  const adminRoles = new Set(['admin', 'platform admin', 'platform_admin'])
  return currentUser.roles.some((role) => adminRoles.has(role.name.toLowerCase()))
}

/**
 * Check if error is likely due to RBAC filtering (empty result vs permission denied)
 *
 * @param error - Error object
 * @param totalItems - Total items count from API
 * @returns True if likely RBAC-related empty result
 */
export function isLikelyRbacFiltered(error: unknown, totalItems: number): boolean {
  // If no error but totalItems is 0, might be RBAC filtered
  // This is a heuristic - we can't be 100% sure without backend metadata
  return !error && totalItems === 0
}
